import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import React from 'react';

import CopyIcon from '@/assets/icons/copy.svg?react';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';

interface Props {
  readonly value: string;
  readonly previewLines?: number;
  readonly successMessageKey?: string;
}

export const CollapsibleValue: React.FC<Props> = (props: Props): React.ReactElement => {
  const { value, previewLines = 2, successMessageKey = 'COPIED_TO_CLIPBOARD' } = props;
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
  const toast = useToast();
  const tr = useTranslator();

  const lines = React.useMemo((): readonly string[] => value.split('\n'), [value]);
  const needsCollapse = lines.length > previewLines;

  const previewText = React.useMemo((): string => {
    if (!needsCollapse || isExpanded) {
      return value;
    }
    return lines.slice(0, previewLines).join('\n') + '...';
  }, [value, lines, previewLines, needsCollapse, isExpanded]);

  const onCopy = React.useCallback((): void => {
    navigator.clipboard.writeText(value).then((): void => {
      toast.show(tr(successMessageKey), 'success');
    });
  }, [value, toast, tr, successMessageKey]);

  const onToggle = React.useCallback((): void => {
    setIsExpanded((prev: boolean): boolean => !prev);
  }, []);

  return (
    <Box>
      <Box display="flex" alignItems="flex-start">
        <Box
          component="pre"
          sx={{
            fontFamily: 'monospace',
            fontSize: '12px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            margin: 0,
            padding: 1,
            backgroundColor: 'action.hover',
            color: 'text.primary',
            borderRadius: 1,
            flex: 1,
            maxHeight: isExpanded ? '300px' : 'none',
            overflowY: isExpanded ? 'auto' : 'hidden',
          }}>
          {previewText}
        </Box>
        <Box display="flex" alignItems="center" ml={1}>
          <IconButton color="primary" size="small" onClick={onCopy}>
            <CopyIcon fill="currentColor" width={16} height={16} />
          </IconButton>
        </Box>
      </Box>
      {needsCollapse && (
        <Link
          component="button"
          variant="body2"
          onClick={onToggle}
          sx={{ mt: 0.5, cursor: 'pointer' }}>
          {isExpanded ? tr('SHOW_LESS') : tr('SHOW_MORE')}
        </Link>
      )}
    </Box>
  );
};
